import { IAdminUser } from '@dadohr/shared/domainTypes/adminUser';
import { ICurrentUser } from '@dadohr/shared/domainTypes/currentUser';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { useEffect, useState } from 'react';
import {
  clearSentryUser,
  setSentryOrg,
  setSentryUser
} from '../../sentry';

let currentUser: ICurrentUser | null = null;
type CurrentUserListener = (nextCurrentUser: ICurrentUser | null) => void;

const listeners: CurrentUserListener[] = [];

export const setCurrentUser = (nextCurrentUser: ICurrentUser | null) => {
  currentUser = nextCurrentUser;

  // Update Sentry context when user/org changes
  if (nextCurrentUser) {
    setSentryUser(
      nextCurrentUser.id,
      nextCurrentUser.email,
      nextCurrentUser.displayName || undefined
    );
    setSentryOrg(nextCurrentUser.org.id, nextCurrentUser.org.name);
  } else {
    clearSentryUser();
  }

  listeners.forEach((listener) => listener(nextCurrentUser));
};

// This should only be called after setCurrentUser was called with a non-null value!
export const getCurrentUser = () => {
  return currentUser!;
};

const listenToCurrentUserChanges = (
  listener: CurrentUserListener
): (() => void) => {
  listeners.push(listener);
  return () => {
    const index = listeners.indexOf(listener);
    if (index !== -1) {
      listeners.splice(index, 1);
    }
  };
};

// TODO - this needs to be done context based as well.
// This would allow us to use components which rely on this
// hook in the admin app, where we could just provide a different current user
export const useCurrentUser = () => {
  const [currentUser, setCurrentUser] = useState<ICurrentUser | null>(
    getCurrentUser()
  );

  useEffect(() => listenToCurrentUserChanges(setCurrentUser), []);
  // Can never be null - as this is just used inside the app,
  // if it ever IS null, then we would immediately move to the Login page anyway
  return currentUser!;
};

export const createCurrentUser = (
  authUser: firebase.User,
  user: IAdminUser,
  org: IOrganization,
  tz: string = 'Europe/Berlin'
): ICurrentUser => {
  return {
    id: authUser.uid,
    ...user,
    email: user.email || authUser.email || '',
    photoURL: user.avatar || authUser.photoURL || '',
    displayName: authUser.displayName,
    authUser,
    org,
    tz
  };
};

export const isDadoEmailAddress = (email: string | null | undefined) => {
  return !!(
    email?.endsWith('dadohr.com') || email?.endsWith('emplexp@gmail.com')
  );
};

export const useIsCurrentUserDadoEmployee = () => {
  const currentUser = useCurrentUser();
  return isDadoEmailAddress(currentUser?.email);
};
